import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDrawerModule, NzDrawerRef, NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';

import {
  BroadcastDeliveryResult,
  BroadcastLabelInfo,
  BroadcastTaskDetail,
  BroadcastTaskListItem,
  PersonalBroadcastService
} from '../personal-broadcast.service';

type BroadcastListViewItem = {
  id: string;
  title: string;
  remark: string;
  status: string;
  statusLabel: string;
  time: string;
  channel: string;
  channelLabel: string;
  labelInfos: BroadcastLabelInfo[];
  expected: number;
  success: number;
  failed: number;
};

type BroadcastDetailMessageView =
  | { type: 'text'; text: string }
  | { type: 'image'; name: string; size: number; url?: string; thumbnailUrl?: string }
  | { type: 'file'; name: string; size: number; url?: string; mimeType?: string }
  | { type: 'template'; title: string; subtitle: string; imageUrl?: string; buttonTexts: string[] }
  | { type: 'unknown'; rawType: string };

@Component({
  selector: 'personal-broadcast-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    I18nPipe,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzCardModule,
    NzTagModule,
    NzTableModule,
    NzSelectModule,
    NzSpinModule,
    NzEmptyModule,
    NzToolTipModule,
    NzModalModule,
    NzDrawerModule,
    TestIdDirective
  ],
  templateUrl: './personal-broadcast-list.component.html',
  styleUrls: ['./personal-broadcast-list.component.less']
})
export class PersonalBroadcastListComponent implements OnInit {
  @Input() tenantIdInput = '';
  @Input() serviceNumberIdInput = '';
  @Input() serviceNumberOpenTypeInput = '';
  @Input() embedded = false;
  @ViewChild('detailModalTitleTpl', { static: true }) detailModalTitleTpl!: TemplateRef<{}>;
  @ViewChild('detailModalTpl', { static: true }) detailModalTpl!: TemplateRef<{}>;

  private readonly location = inject(Location);

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly personalBroadcastService = inject(PersonalBroadcastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modal = inject(NzModalService);
  private readonly drawer = inject(NzDrawerService);
  private readonly message = inject(NzMessageService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  tenantId = '';
  loading = false;
  loadingMore = false;
  hasMore = true;
  keywords = '';
  list: BroadcastListViewItem[] = [];
  totalCount = 0;
  private readonly maxLabelToShow = 3;
  private readonly pageSize = 20;
  private pageIndex = 0;

  detailModalItem: BroadcastListViewItem | null = null;
  detailLoading = false;
  detail: BroadcastTaskDetail | null = null;
  detailMessages: BroadcastDetailMessageView[] = [];
  deliveryResults: BroadcastDeliveryResult[] = [];
  deliveryTotal = 0;
  deliveryPageIndex = 1;
  deliveryPageSize = 10;
  deliveryStatusFilter = '';
  deliveryLoading = false;
  private detailModalRef: NzModalRef | null = null;
  private deliveryDrawerRef: NzDrawerRef | null = null;
  @ViewChild('deliveryDrawerTpl', { static: true }) deliveryDrawerTpl!: TemplateRef<any>;
  deliveryDrawerItem: BroadcastListViewItem | null = null;

  ngOnInit(): void {
    this.tenantId = this.tenantIdInput || this.route.snapshot.paramMap.get('id') || '';
    this.loadList(true);
  }

  get filteredList(): any[] {
    const keyword = this.keywords.trim();
    if (!keyword) return this.list;
    return this.list.filter(item => item.title.includes(keyword));
  }

  get recordCount(): number {
    return this.totalCount || this.list.length;
  }

  create(): void {
    this.router.navigate(['/personal-broadcast-detail', this.tenantId], { queryParams: this.getNavigationQueryParams() });
  }

  view(item: BroadcastListViewItem): void {
    this.openDetailModal(item);
  }

  openDeliveryDrawer(item: BroadcastListViewItem): void {
    this.deliveryDrawerItem = item;
    this.deliveryResults = [];
    this.deliveryTotal = 0;
    this.deliveryPageIndex = 1;
    this.deliveryStatusFilter = '';
    this.deliveryDrawerRef = this.drawer.create({
      nzTitle: this.i18n.fanyi('personal-broadcast.detail.section.delivery-results'),
      nzContent: this.deliveryDrawerTpl,
      nzWidth: 760,
      nzMaskClosable: true
    });
    this.deliveryDrawerRef.afterClose.subscribe(() => this.clearDeliveryDrawerState());
    this.loadDeliveryResults();
  }

  private clearDeliveryDrawerState(): void {
    this.deliveryDrawerRef = null;
    this.deliveryDrawerItem = null;
    this.deliveryResults = [];
    this.deliveryTotal = 0;
    this.deliveryLoading = false;
    this.cdr.markForCheck();
  }

  edit(item: BroadcastListViewItem): void {
    if (!this.tenantId) return;
    if (!this.canEdit(item)) return;
    this.router.navigate(['/personal-broadcast-detail', this.tenantId, item.id], { queryParams: this.getNavigationQueryParams() });
  }

  editFromDetail(): void {
    const item = this.detailModalItem;
    if (!item || !this.tenantId || !this.canEdit(item)) return;
    this.detailModalRef?.close();
    this.clearDetailModalState();
    this.router.navigate(['/personal-broadcast-detail', this.tenantId, item.id], { queryParams: this.getNavigationQueryParams() });
  }

  private getNavigationQueryParams(): Record<string, string> {
    const queryParams: Record<string, string> = {};
    if (this.serviceNumberIdInput) queryParams['serviceNumberId'] = this.serviceNumberIdInput;
    if (this.serviceNumberOpenTypeInput) queryParams['serviceNumberOpenType'] = this.serviceNumberOpenTypeInput;
    if (this.embedded) queryParams['returnUrl'] = this.location.path(true) || this.router.url;
    return queryParams;
  }

  canEdit(item: BroadcastListViewItem): boolean {
    return String(item?.status || '').trim() === 'Will';
  }

  canDelete(item: BroadcastListViewItem): boolean {
    return String(item?.status || '').trim() !== 'Doing';
  }

  remove(item: BroadcastListViewItem): void {
    if (!this.canDelete(item)) return;
    const id = String(item?.id || '').trim();
    if (!id) return;
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('personal-broadcast.list.confirm.delete-title'),
      nzContent: this.i18n.fanyi('personal-broadcast.list.confirm.delete-content'),
      nzOkDanger: true,
      nzOkText: this.i18n.fanyi('personal-broadcast.list.confirm.delete-ok'),
      nzCancelText: this.i18n.fanyi('personal-broadcast.list.confirm.delete-cancel'),
      nzOnOk: () => {
        this.loading = true;
        this.cdr.markForCheck();
        this.personalBroadcastService
          .deleteBroadcastTask(id)
          .pipe(
            finalize(() => {
              this.loading = false;
              this.cdr.markForCheck();
            })
          )
          .subscribe({
            next: () => {
              this.message.success(this.i18n.fanyi('personal-broadcast.list.message.delete-success'));
              this.loadList(true);
            },
            error: (err: any) => {
              const msg = String(err?.message || '').trim();
              this.message.error(
                msg
                  ? this.i18n.fanyi('personal-broadcast.list.message.delete-failed-with-msg', { msg })
                  : this.i18n.fanyi('personal-broadcast.list.message.delete-failed')
              );
            }
          });
      }
    });
  }

  onListScroll(event: Event): void {
    const el = event.target as HTMLElement | null;
    if (!el) return;
    if (this.loading || this.loadingMore || !this.hasMore) return;
    const threshold = 120;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (!nearBottom) return;
    this.loadList(false);
  }

  statusColor(status: string): string {
    const value = String(status || '').trim();
    if (value === 'Done') return 'green';
    if (value === 'Doing') return 'blue';
    if (value === 'Will') return 'default';
    if (value === 'Failed' || value === 'Fail') return 'red';
    return 'default';
  }

  statusText(status: string): string {
    const value = String(status || '').trim();
    if (value === 'Will') return this.i18n.fanyi('personal-broadcast.status.will');
    if (value === 'Doing') return this.i18n.fanyi('personal-broadcast.status.doing');
    if (value === 'Done') return this.i18n.fanyi('personal-broadcast.status.done');
    return value || '—';
  }

  statusIcon(status: string): string {
    const value = String(status || '').trim();
    if (value === 'Done') return 'check';
    if (value === 'Will') return 'clock-circle';
    if (value === 'Doing') return 'send';
    if (value === 'Failed' || value === 'Fail') return 'close';
    return 'info-circle';
  }

  statusClass(status: string): string {
    const value = String(status || '').trim();
    if (value === 'Done') return 'status-done';
    if (value === 'Will') return 'status-will';
    if (value === 'Doing') return 'status-doing';
    if (value === 'Failed' || value === 'Fail') return 'status-failed';
    return 'status-default';
  }

  channelIcon(channel: string): string | null {
    const value = String(channel || '')
      .trim()
      .toLowerCase();
    if (value === 'line') return 'assets/images/channels/icon-im-line.svg';
    if (value === 'facebook') return 'assets/images/channels/icon-im-fb.svg';
    if (value === 'instagram') return 'assets/images/channels/icon-im-ig.svg';
    return null;
  }

  channelText(channel: string): string {
    const value = String(channel || '').trim();
    if (!value) return '—';
    if (value.toLowerCase() === 'line') return 'LINE';
    if (value.toLowerCase() === 'preset') return this.i18n.fanyi('personal-broadcast.channel.preset');
    return value;
  }

  shouldShowCounts(item: BroadcastListViewItem): boolean {
    return String(item?.status || '').trim() === 'Done';
  }

  visibleLabels(item: BroadcastListViewItem): BroadcastLabelInfo[] {
    return (item?.labelInfos || []).slice(0, this.maxLabelToShow);
  }

  hiddenLabelCount(item: BroadcastListViewItem): number {
    const len = item?.labelInfos?.length || 0;
    return len > this.maxLabelToShow ? len - this.maxLabelToShow : 0;
  }

  hiddenLabelNames(item: BroadcastListViewItem): string {
    const hidden = (item?.labelInfos || [])
      .slice(this.maxLabelToShow)
      .map(x => x.name)
      .filter(Boolean);
    return hidden.join('、');
  }

  formatFileSize(size: number): string {
    const n = Number(size || 0);
    if (!Number.isFinite(n) || n <= 0) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(2)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  }

  private openDetailModal(item: BroadcastListViewItem): void {
    if (!this.tenantId) return;
    this.detailModalItem = item;
    this.detailLoading = true;
    this.detail = null;
    this.detailMessages = [];
    this.deliveryResults = [];
    this.deliveryTotal = 0;
    this.deliveryPageIndex = 1;
    this.deliveryStatusFilter = '';
    this.cdr.markForCheck();

    this.detailModalRef = this.modal.create({
      nzTitle: this.detailModalTitleTpl,
      nzContent: this.detailModalTpl,
      nzWidth: 760,
      nzMaskClosable: true,
      nzFooter: null,
      nzOnCancel: () => {
        this.clearDetailModalState();
      }
    });

    this.personalBroadcastService
      .getBroadcastTaskDetail(this.tenantId, item.id, this.serviceNumberIdInput)
      .pipe(
        finalize(() => {
          this.detailLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: detail => {
          this.detail = detail;
          this.detailMessages = this.buildDetailMessages(detail);
          this.cdr.markForCheck();
        },
        error: () => {
          this.detail = null;
          this.detailMessages = [];
          this.cdr.markForCheck();
        }
      });
  }

  private clearDetailModalState(): void {
    this.detailModalRef = null;
    this.detailModalItem = null;
    this.detail = null;
    this.detailMessages = [];
    this.deliveryResults = [];
    this.deliveryTotal = 0;
    this.detailLoading = false;
    this.cdr.markForCheck();
  }

  loadDeliveryResults(): void {
    const broadcastId = String(this.deliveryDrawerItem?.id || '').trim();
    if (!broadcastId) return;
    this.deliveryLoading = true;
    this.personalBroadcastService
      .listBroadcastDeliveryResults({
        broadcastId,
        pageIndex: this.deliveryPageIndex - 1,
        pageSize: this.deliveryPageSize,
        status: this.deliveryStatusFilter || undefined
      })
      .pipe(
        finalize(() => {
          this.deliveryLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(result => {
        this.deliveryResults = result.items;
        this.deliveryTotal = result.totalCount;
      });
  }

  onDeliveryStatusChange(status: string): void {
    this.deliveryStatusFilter = status || '';
    this.deliveryPageIndex = 1;
    this.loadDeliveryResults();
  }

  onDeliveryPageIndexChange(pageIndex: number): void {
    this.deliveryPageIndex = pageIndex;
    this.loadDeliveryResults();
  }

  deliveryStatusLabel(status: string): string {
    return ({ Pending: '待發送', Success: '成功', Failed: '失敗', Interrupted: '已中斷' } as Record<string, string>)[status] || status;
  }

  deliveryTime(value?: number): string {
    return value ? this.formatDateTime(new Date(value)) : '—';
  }

  private buildDetailMessages(detail: BroadcastTaskDetail | null): BroadcastDetailMessageView[] {
    const list = Array.isArray(detail?.content) ? (detail?.content as any[]) : [];
    return list.map(raw => this.mapDetailMessage(raw)).filter(Boolean) as BroadcastDetailMessageView[];
  }

  private mapDetailMessage(raw: any): BroadcastDetailMessageView | null {
    const rawType = String(raw?.type ?? '')
      .trim()
      .toLowerCase();
    const contentRaw = raw?.content ?? raw?.data ?? raw?.payload ?? raw;
    const contentObj = this.safeJsonParse(contentRaw);

    if (rawType.includes('text') || String(contentObj?.type || '').toLowerCase() === 'text') {
      const text = String(contentObj?.text ?? contentObj?.content ?? contentRaw ?? '').trim();
      if (!text) return null;
      return { type: 'text', text };
    }
    if (rawType.includes('image') || String(contentObj?.type || '').toLowerCase() === 'image') {
      const name = String(contentObj?.name ?? '').trim() || this.i18n.fanyi('personal-broadcast.detail.message.image-default');
      const size = Number(contentObj?.size ?? 0);
      const url = String(contentObj?.url ?? contentObj?.fileUrl ?? contentObj?.downloadUrl ?? '').trim() || undefined;
      const thumbnailUrl = String(contentObj?.thumbnailUrl ?? contentObj?.thumbUrl ?? '').trim() || undefined;
      return { type: 'image', name, size: Number.isFinite(size) ? size : 0, url, thumbnailUrl };
    }
    if (rawType.includes('file') || String(contentObj?.type || '').toLowerCase() === 'file') {
      const name = String(contentObj?.name ?? '').trim() || this.i18n.fanyi('personal-broadcast.detail.message.file-default');
      const size = Number(contentObj?.size ?? 0);
      const mimeType = String(contentObj?.mimeType ?? contentObj?.type ?? '').trim() || undefined;
      const url = String(contentObj?.url ?? contentObj?.fileUrl ?? contentObj?.downloadUrl ?? '').trim() || undefined;
      return { type: 'file', name, size: Number.isFinite(size) ? size : 0, url, mimeType };
    }
    if (rawType.includes('template') || String(contentObj?.type || '').toLowerCase() === 'carousel') {
      const title = String(contentObj?.title ?? '').trim();
      const subtitle = String(contentObj?.subTitle ?? contentObj?.subtitle ?? '').trim();
      const imageUrl = String(contentObj?.imageUrl ?? contentObj?.image ?? '').trim() || undefined;
      const actions = Array.isArray(contentObj?.actions) ? contentObj.actions : [];
      const buttonTexts = actions.map((a: any) => String(a?.label ?? a?.text ?? '').trim()).filter(Boolean);
      return {
        type: 'template',
        title: title || this.i18n.fanyi('personal-broadcast.detail.message.template-default'),
        subtitle,
        imageUrl,
        buttonTexts
      };
    }
    const typeText = String(raw?.type ?? contentObj?.type ?? '').trim();
    return { type: 'unknown', rawType: typeText || 'unknown' };
  }

  private safeJsonParse(input: any): any {
    if (input == null) return null;
    if (typeof input === 'object') return input;
    const str = String(input || '').trim();
    if (!str) return null;
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  }

  private loadList(reset: boolean): void {
    if (!this.tenantId) return;
    if (reset) {
      this.pageIndex = 0;
      this.totalCount = 0;
      this.list = [];
      this.hasMore = true;
      this.loading = true;
    } else {
      this.loadingMore = true;
    }
    this.cdr.markForCheck();
    this.personalBroadcastService
      .listBroadcastTasks({
        tenantId: this.tenantId,
        serviceNumberId: this.serviceNumberIdInput || undefined,
        pageIndex: this.pageIndex,
        pageSize: this.pageSize
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.loadingMore = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: res => {
          const incoming = (res.items || []).map(item => this.toViewItem(item));
          this.totalCount = Number(res.totalCount || 0);
          this.list = reset ? incoming : [...this.list, ...incoming];
          if (incoming.length > 0) this.pageIndex += 1;
          if (this.totalCount > 0) {
            this.hasMore = this.list.length < this.totalCount;
          } else {
            this.hasMore = incoming.length >= this.pageSize;
          }
          this.cdr.markForCheck();
        },
        error: () => {
          if (reset) {
            this.list = [];
            this.totalCount = 0;
          }
          this.hasMore = false;
          this.cdr.markForCheck();
        }
      });
  }

  private toViewItem(item: BroadcastTaskListItem): BroadcastListViewItem {
    const time = item.broadcastTime ? this.formatDateTime(new Date(item.broadcastTime)) : '—';
    const channelLabel = this.channelText(item.channel);
    return {
      id: item.id,
      title: item.title,
      remark: item.remark,
      status: item.status,
      statusLabel: this.statusText(item.status),
      time,
      channel: item.channel,
      channelLabel,
      labelInfos: item.labelInfos || [],
      expected: item.expectedCount,
      success: item.successCount,
      failed: item.failedCount
    };
  }

  private formatDateTime(value: Date): string {
    const date = value instanceof Date ? value : new Date(value);
    const pad2 = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
  }
}
